import { Divider, Flex, Input, Space, Stack, Text, Title } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import type { PropsWithChildren } from "react";
import { Grade, gradeTranslationsType1 } from "../../../models/enums/grade";
import { GradeRadio } from "../../radio/GradeRadio";
import type { EducationReportFormType } from "../form/EducationReportForm";
import type { EducationReportSelfAssessmentFormType } from "../selfAssessment/EducationReportSelfAssessmentForm";

export type GradingField<T> = {
  title: string;
  description?: string;
  key: keyof T;
  selfAssessment?: Grade;
};

type GradingSectionProps<T> = PropsWithChildren<{
  baseNumber: number;
  fields: GradingField<T>[];
  form: UseFormReturnType<EducationReportFormType | EducationReportSelfAssessmentFormType>;
  baseKey: "data" | "self_assessment";
  path: string;
  title?: string;
  disableNumbering?: boolean;
  gradeTranslations?: Record<Grade, string>;
  displaySelfAssessment?: boolean;
  onObeservationLookup?: () => void;
}>;

export function GradingSection<T>({
  title,
  disableNumbering = false,
  path,
  baseNumber,
  fields,
  form,
  gradeTranslations = gradeTranslationsType1,
  baseKey,
  children,
  displaySelfAssessment = false,
}: GradingSectionProps<T>) {
  const mappedFields = fields.map((field, index) => (
    <Stack key={field.key.toString()}>
      <Flex>
        <Stack flex={3.8}>
          <Title order={6}>{disableNumbering ? field.title : `${baseNumber}.${index + 1} ${field.title}`}</Title>
          {field.description && <Text>{field.description}</Text>}
        </Stack>
        <GradeRadio
          selfAssessmentValue={displaySelfAssessment ? field.selfAssessment : undefined}
          onReset={() => form.setFieldValue(`${baseKey}.${path}.${field.key.toString()}`, "")}
          key={form.key(`${baseKey}.${path}.${field.key.toString()}`)}
          {...form.getInputProps(`${baseKey}.${path}.${field.key.toString()}`)}
        />
      </Flex>
      <Divider />
    </Stack>
  ));

  return (
    <Stack>
      <Title order={5}>{title}</Title>
      <Flex>
        <Space flex={3.8} />
        <Input.Label ta="center" flex={1}>
          {gradeTranslations[Grade.A]}
        </Input.Label>
        <Input.Label ta="center" flex={1}>
          {gradeTranslations[Grade.B]}
        </Input.Label>
        <Input.Label ta="center" flex={1}>
          {gradeTranslations[Grade.C]}
        </Input.Label>
        <Input.Label ta="center" flex={1}>
          {gradeTranslations[Grade.D]}
        </Input.Label>
        <Space w={28} />
      </Flex>
      {mappedFields}
      {children}
    </Stack>
  );
}
